<template>
  <div
    :id="`category_${listGroup.id}`"
    :class="{
      'list-group__sticky': stickyCategoryId === `category_${listGroup.id}` && !isOpen,
    }"
  >
    <m-confirm ref="confirm" mode="danger" />
    <div class="list-group">
      <div class="list-group__left-side">
        <toggle-button
          v-model="isOpen"
          class="documents-group__toggler"
          :disabled="!canGroupBeOpened"
          disabled-popover="Пустая категория"
        />
        <div class="list-group__label">
          {{ listGroup.name }}
        </div>
      </div>
      <div class="list-group__actions">
        <c-button
          v-b-popover.html="{
            container: '#app',
            trigger: 'hover blur',
            boundary: 'window',
            content: 'Редактировать',
            placement: 'top',
          }"
          size="iconic"
          icon="edit"
          @click="onGroupEdit"
        />
        <div
          :id="`list-group-${listGroup.id}`"
          class="list-group__actions-remove"
        >
          <c-button
            v-b-popover.html="{
              container: '#app',
              trigger: 'hover blur',
              boundary: 'window',
              content: 'Удалить',
              placement: 'top',
            }"
            size="iconic"
            :icon="{ icon: 'trash', color: 'secondary' }"
            :disabled="cannotBeDeleted"
            class="list-group__actions-remove"
            @click="deleteListGroup"
          />
        </div>
        <c-button
          v-b-popover.html="{
            container: '#app',
            trigger: 'hover blur',
            boundary: 'window',
            content: 'Переместить',
            placement: 'top',
          }"
          size="iconic"
          icon="arrows-constructor"
        />

        <b-popover v-if="cannotBeDeleted" triggers="hover blur" :target="`list-group-${listGroup.id}`">
          <error-feedback
            text="В данный момент удаление невозможно, так как категория содержит данные. Сначала перенесите все данные из
            категории"
          />
        </b-popover>
      </div>
    </div>

    <slide-up-down
      class="list-group__draggable"
      :active="isOpen"
      :duration="400"
      @open-start="onGroupOpen"
    >
      <draggable
        v-if="!isLoading"
        v-model="listGroupItemsSorted"
        group="type"
        :force-fallback="true"
        ghost-class="sortable__ghost"
        @change="changeHandler"
        @update="updateHandler"
      >
        <list-group-item
          v-for="item in listGroupItemsSorted"
          :key="item.id"
          :list-group-item="item"
          :selected-list-group-items-status="selectedListGroupItemsStatus"
          @edit-list-group-item="onListGroupItemEdit"
          @on-delete-list-group-item="$emit('on-delete-list-group-item')"
        />
      </draggable>

      <c-loader v-else :active="true" height="80" />
    </slide-up-down>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import draggable from "vuedraggable";
import SlideUpDown from "vue-slide-up-down";

import ErrorFeedback from "@/components/error-feedback";
import ToggleButton from "@/components/controls/toggle-button";
import Button from "@/components/controls2/Button";
import Loader from "@/components/loader";
import Confirm from "@/components/modals/confirm-modal";

import listGroupItem from "./list-group-item";

const moveCategoryHeaderToParent = (groupId) => {
  const header = document.querySelector(`#category_${groupId} .list-group`).cloneNode(true);
  const parentList = document.querySelector("#list div");
  header.classList.add("list-group__sticky");
  header.id = `opened-category-${groupId}`;
  parentList.prepend(header);
};

const removeCategoryHeaderFromParent = (groupId) => {
  const header = document.getElementById(`opened-category-${groupId}`);
  if (header) {
    header.remove();
  }
};

export default {
  name: "list-group",
  components: {
    ToggleButton,
    SlideUpDown,
    draggable,
    listGroupItem,
    ErrorFeedback,
    "c-button": Button,
    "c-loader": Loader,
    "m-confirm": Confirm,
  },
  props: {
    listGroup: {
      type: Object,
      default: () => ({}),
    },
    selectedListGroupItemsStatus: {
      type: String,
      default: "all",
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    stickyCategoryId: {
      type: String,
      default: "",
    },
  },
  mounted() {
    this.listGroupItemsSorted = [...this.listGroup.items].sort((a, b) => b.sort - a.sort)
  },
  data() {
    return {
      isOpen: false,
      isLoading: false,
      fetchedItems: false,
      items: [],
      listGroupItemsSorted: [],
    };
  },
  computed: {
    ...mapState({
      lastListGroupIdOfCreatedItem: (state) => state.referenceLoadTypes.lastListGroupIdOfCreatedItem,
      lastChangeWasGroupSorting: (state) => state.referenceLoadTypes.lastChangeWasGroupSorting,
    }),
    ...mapGetters({
      getListGroupItemsByGroup: "referenceLoadTypes/getListGroupItemsByGroupId",
    }),
    doesGroupContainActiveItems() {
      return this.listGroup.items.some((i) => i.is_archive.toString() === "0");
    },
    doesGroupContainArchivedItems() {
      return this.listGroup.items.some((i) => i.is_archive.toString() === "1");
    },
    doesGroupHaveItems() {
      const { hasFouls, hasArchivedItems, hasActiveItems } = this.listGroup;
      if (this.areAllItemsSelected) {
        return !!hasFouls || !!hasActiveItems || !!hasArchivedItems;
      }
      if (this.areActiveItemsSelected) {
        return hasActiveItems !== 0;
      }
      if (this.areArchivedItemsSelected) {
        return hasArchivedItems !== 0;
      }
      return false;
    },
    areActiveItemsSelected() {
      return this.selectedListGroupItemsStatus === "active";
    },
    areArchivedItemsSelected() {
      return this.selectedListGroupItemsStatus === "archived";
    },
    areAllItemsSelected() {
      return this.selectedListGroupItemsStatus === "all";
    },
    cannotBeDeleted() {
      return !!this.listGroup.items.length;
    },
    canGroupBeOpened() {
      if (this.listGroup.items.length) {
        return true;
      }
      return false;
    },
  },
  watch: {
    "lastListGroupIdOfCreatedItem": function (value) {
      if (value === this.listGroup.id) {
        this.isOpen = true;
      }
    },
    "stickyCategoryId": function (value) {
      if (value === `category_${this.listGroup.id}` && this.isOpen) {
        moveCategoryHeaderToParent(this.listGroup.id);
        return;
      }
      removeCategoryHeaderFromParent(this.listGroup.id);
    },
    "doesGroupHaveItems": function (value) {
      if (value) {
        this.isOpen = true;
        return;
      }
      this.isOpen = false;
    },
    "listGroup.items": function (items) {
      if (this.lastChangeWasGroupSorting) {
        return;
      }

      if (this.lastListGroupIdOfCreatedItem === this.listGroup.id) {
        this.isOpen = true;
      }

      if (this.areAllItemsSelected) {
        if (items.length) {
          this.listGroup.hasFouls = 1;
          return;
        }
        this.listGroup.hasFouls = 0;
      }
      if (this.areActiveItemsSelected) {
        if (items.length) {
          this.listGroup.hasFouls = 1;
          this.listGroup.hasActiveItems = 1;
          return;
        }
        this.$store.commit("referenceLoadTypes/deleteListGroup", { id: this.listGroup.id, name: this.listGroup.name });
      }
      if (this.areArchivedItemsSelected) {
        if (items.length) {
          this.listGroup.hasFouls = 1;
          this.listGroup.hasArchivedItems = 1;
          return;
        }
        if (this.listGroup.hasArchivedItems === 0) {
          this.$store.commit("referenceLoadTypes/deleteListGroup", { id: this.listGroup.id, name: this.listGroup.name });
        }
      }
    },
    "listGroup.items": {
      handler() {
        this.listGroupItemsSorted = [...this.listGroup.items].sort((a, b) => b.sort - a.sort)
      },
      deep: true
    },
  },
  methods: {
    updateHandler(e) {
      let newList = e.to.__vue__.$slots.default;
      let sortingData = [];

      newList.forEach((item, i) => {
        sortingData.push({id: item.key, sort: newList.length - i})
      })

      this.$store.dispatch("referenceLoadTypes/changeListGroupItemsSort", sortingData);
    },
    changeHandler(e) {
      if (e.added) {
        this.$store.dispatch("referenceLoadTypes/changeListItemCategory", {
          categoryId: this.listGroup.id,
          listItem: e.added.element,
        });
      }
    },
    async onGroupOpen() {
      // if (this.items.length > 1 || !this.doesGroupHaveItems) {
      //   return;
      // }

      // this.isLoading = true;
      // await this.$store.dispatch("referenceLoadTypes/fetchViolationGroups", {
      //   categories: [this.listGroup.id],
      //   // status: this.selectedListGroupItemsStatus,
      //   ...this.filters,
      // });
      // this.fetchedItems = true;
      // this.isLoading = false;
    },
    onGroupEdit() {
      this.$emit("edit-group", this.listGroup.id);
    },
    onListGroupItemEdit(params) {
      this.$emit("edit-list-group-item", params);
    },
    deleteListGroup() {
      this.$refs.confirm.open({
        title: "Удаление категории",
        text: `Категория <b>${this.listGroup.name}</b> будет удалена без возможности восстановления`,
        confirm: "Удалить",
        onConfirm: () => {
          this.$store.dispatch("referenceLoadTypes/deleteListGroup", { id: this.listGroup.id, name: this.listGroup.name });
          this.$emit('on-delete-list-group');
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/vars.scss";

.sortable__ghost {
  padding: 0 !important;
  height: 0 !important;
  min-height: 0;
  border: 5px solid $color-primary !important;
  border-radius: 3px;
  content-visibility: hidden;
}

.list-group {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 13px;
  margin-top: -1px;
  min-height: 48px;
  max-width: 1190px;
  justify-content: space-between;
  background-color: #fff;
  border: 1px solid $color-border-alt;

  &__sticky {
    position: sticky;
    top: -33px;
    z-index: 100;
    border-bottom: 1px solid #d3d8df !important;
    box-shadow: 0 10px 6px -5px rgba(0, 0, 0, 0.15);
  }

  @include onlyExtraSmall {
    border-left: none;
    border-right: none;
  }

  &__draggable {
    padding-left: 16px;
  }

  &__left-side {
    display: flex;
    align-items: center;
    & button {
      margin-right: 10px;
    }
  }

  &__label {
    font-size: 15px;
    font-weight: 500;
    line-height: 1.08;
  }

  &__actions {
    display: flex;
    align-items: center;

    & button {
      margin-left: 10px;
    }
    &-remove {
      margin-top: -1px;
    }
  }
}
</style>
